
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";
import { ActionPlan, SelectedActionPlan, TaskType, ActionPlanActions, ActionPlanCategory } from "@/store/tasks/types";
import { GuardTour, GuardTourStep, GuardTourType } from "@/store/guard-tours/types";
import vSelect from "vue-select";
import vselect3 from "vselect3";
import api from "@/services/api.service";
import { get, clone, cloneDeep } from "lodash";
import { FeaturesList } from "@/store/types";

const Tasks = namespace("tasks");
const GuardTours = namespace("GuardTours");

@Component({
	components: {
        "v-select": vSelect,
        "v-select3": vselect3,
	}
})
export default class ActionPlanLinker extends Vue {

	@Getter("getFeaturesList") featuresList: FeaturesList;

	@Tasks.State(state => state.ActionPlans) actionPlans: ActionPlan[];
	@Tasks.State(state => state.TaskTypes) taskTypes: TaskType[];
	@Tasks.Action fetchActionPlans: () => Promise<void>;

	@GuardTours.State filterTourTypeId: number;

	private selectedActionPlan: SelectedActionPlan = null;
	private isActionPlanModalVisible: boolean = false;
	private currentStep: GuardTourStep = null;

	@Prop()
	public tourStep: GuardTourStep;

	@Emit("link-plan-to-step")
	public linkPlanToStep(tourStepId:number, scriptId: number){}

	private get assessmentActionPlans() {
		return this.actionPlans.filter(x => x.scriptCategoryID === ActionPlanCategory.Assessment);
	}

	private get dispatchAlarmActionPlans() {
		return this.actionPlans.filter(x => 
			x.scriptCategoryID === ActionPlanCategory.Dispatch || 
			x.scriptCategoryID === ActionPlanCategory.Alarm ||
			x.scriptCategoryID === null
		);
	}

	private get isLinkActionEnabled(): boolean {
		return get(this.featuresList, ["Mobile", "GuardTours", "ActionPlans"], false);
	}

	private async showActionPlanModal(tourStep: GuardTourStep): Promise<void> {
		this.isActionPlanModalVisible = true;
		this.currentStep = tourStep;
		this.selectedActionPlan = null;

		// if the selected step has an action plan assigned, set the details for the drop down
		if (this.currentStep.scriptId) {
			this.selectedActionPlan = {
				scriptID: this.currentStep.scriptId,
				title: this.getActionPlanTitle(this.currentStep.scriptId)
			};
		}
	}

    private getActionPlanTitle(scriptId: number): string {
		let title = "";
		if (scriptId && this.loadedActionsPlans) {
			let actionPlan = this.loadedActionsPlans.find((f: ActionPlan) => f.scriptID == scriptId);

			if (actionPlan && actionPlan.title) {
				title = actionPlan.title;
			}
		}

		return title;
	}

    private get loadedActionsPlans(): ActionPlan[] {
		let actionPlansAndActions;
		if (this.filterTourTypeId === GuardTourType.Guard) {
			// cloneDeep because we can't mutate a store property outside of the store
			actionPlansAndActions = cloneDeep(this.dispatchAlarmActionPlans);
		} else if (this.filterTourTypeId === GuardTourType.Assessment) {
			// cloneDeep because we can't mutate a store property outside of the store
			actionPlansAndActions = cloneDeep(this.assessmentActionPlans);
		}

		actionPlansAndActions.forEach(async plan => {
			// get action plan actions
			const result = await api.loadActionPlanActions(plan.scriptID);

			const actions: ActionPlanActions[] = result.data;
			// check if the action plan contains any actions that are not mobile supported
			plan.mobileSupported = actions.every(action => action.mobileSupported);

			plan.title = plan.mobileSupported ? plan.title : plan.title + " - UNAVAILABLE ON MOBILE";
		});

		return actionPlansAndActions;
	}    

    private linkActionPlanToTourSetup(): void {
		if (this.currentStep) {
			if (this.selectedActionPlan) {
				const scriptId = this.selectedActionPlan.scriptID;
				this.linkPlanToStep(this.currentStep.guardTourStepId, scriptId);
			}

			// close the action plan link modal
			this.isActionPlanModalVisible = false;
			this.selectedActionPlan = null;
		}
	}

	private unlinkActionPlan(): void {
		if (this.currentStep) {
			if (this.selectedActionPlan) {
				this.linkPlanToStep(this.currentStep.guardTourStepId, null);
			}

			// close the action plan link modal
			this.isActionPlanModalVisible = false;
			this.selectedActionPlan = null;
		}
	}
}
